import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, IndianRupee, MessageCircle, RefreshCw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useApi } from "@/lib/useApi";
import { useT } from "@/lib/i18n";
import { inr, monthLabel, shiftMonth, todayISO, waLink, reminderText } from "@/lib/format";
import { Card, EmptyState, ErrorState, ListSkeleton, PageTitle } from "@/components/common";
import { Chips } from "@/components/form";
import { StatusPill, statusBar } from "@/components/StatusPill";
import { MarkPaidSheet } from "@/components/MarkPaidSheet";

function MonthPicker({ month, setMonth }: { month: string; setMonth: (m: string) => void }) {
  const { lang } = useT();
  const cur = todayISO().slice(0, 7);
  return (
    <div className="flex items-center justify-between rounded-2xl bg-white border border-slate-200 p-1.5 mb-4">
      <button data-testid="rent-month-prev" onClick={() => setMonth(shiftMonth(month, -1))} className="h-11 w-11 grid place-items-center rounded-xl hover:bg-slate-100"><ChevronLeft className="h-5 w-5" /></button>
      <span data-testid="rent-month-label" className="font-bold">{monthLabel(month, lang, true)}</span>
      <button data-testid="rent-month-next" disabled={month >= cur} onClick={() => setMonth(shiftMonth(month, 1))} className="h-11 w-11 grid place-items-center rounded-xl hover:bg-slate-100 disabled:opacity-30"><ChevronRight className="h-5 w-5" /></button>
    </div>
  );
}

function Summary({ s }: { s: any }) {
  const { t } = useT();
  const cells = [
    { k: "due", v: s.due, c: "text-slate-900" }, { k: "collected", v: s.collected, c: "text-green-700" }, { k: "pending", v: s.pending, c: s.pending ? "text-amber-700" : "text-slate-400" },
  ];
  return (
    <div className="grid grid-cols-3 gap-2 mb-4" data-testid="rent-summary">
      {cells.map((x) => (
        <Card key={x.k} className="p-3">
          <p className="text-[11px] font-semibold text-slate-500">{t(x.k as any)}</p>
          <p data-testid={`rent-summary-${x.k}`} className={`text-base font-extrabold font-mono-num ${x.c}`}>{inr(x.v)}</p>
        </Card>
      ))}
    </div>
  );
}

function RentRow({ r, pg, onPay }: { r: any; pg: string; onPay: () => void }) {
  const { t } = useT();
  return (
    <Card className="p-0 overflow-hidden flex mb-2.5" data-testid={`rent-row-${r.id}`}>
      <div className={`w-1.5 ${statusBar[r.status]}`} />
      <div className="flex-1 p-3.5 flex items-center gap-2 min-w-0">
        <Link to={`/tenants/${r.tenant_id}`} className="flex-1 min-w-0">
          <p className="font-semibold truncate">{r.tenant_name}</p>
          <p className="text-xs text-slate-500 font-mono-num">{t("room")} {r.room_name}-{r.bed_label} · {inr(r.amount_due - r.outstanding)}/{inr(r.amount_due)}{r.is_prorated ? ` · ${t("prorated")}` : ""}</p>
          <div className="mt-1.5"><StatusPill status={r.status} testId={`rent-status-${r.id}`} /></div>
        </Link>
        {r.outstanding > 0 && (
          <div className="flex flex-col gap-1.5 items-end">
            <Button data-testid={`rent-pay-${r.id}`} onClick={onPay} className="h-11 rounded-full bg-emerald-700 hover:bg-emerald-800 px-4">{t("mark_paid")}</Button>
            {r.tenant_phone && (
              <a data-testid={`rent-remind-${r.id}`} href={waLink(r.tenant_phone, reminderText(r.tenant_name, r.outstanding, r.month, pg))} target="_blank" rel="noreferrer" className="h-9 px-3 rounded-full bg-[#25D366]/10 text-[#128C4A] text-xs font-semibold flex items-center gap-1">
                <MessageCircle className="h-3.5 w-3.5" />{t("remind")}
              </a>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

export default function Rent() {
  const { t } = useT();
  const { property } = useAuth();
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [filter, setFilter] = useState("all");
  const [pay, setPay] = useState<any>(null);
  const [gen, setGen] = useState(false);
  const { data, loading, error, reload } = useApi<any>(property ? `/rent?property_id=${property.id}&month=${month}` : null);
  const generate = async () => {
    setGen(true);
    try {
      const r = await api.post("/rent/generate", { property_id: property.id, month });
      toast.success(`${t("generated")} (+${r.data.created})`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setGen(false);
    }
  };
  const items = (data?.items || []).filter((i) => filter === "all" || i.status === filter || (filter === "pending" && i.status === "partial"));
  const genBtn = <Button data-testid="rent-generate-btn" variant="outline" disabled={gen} onClick={generate} className="h-11 rounded-full"><RefreshCw className={`h-4 w-4 mr-1 ${gen ? "animate-spin" : ""}`} />{t("generate_rent")}</Button>;
  return (
    <div>
      <PageTitle title={t("collect_rent")} right={genBtn} />
      <MonthPicker month={month} setMonth={setMonth} />
      {loading && !data ? <ListSkeleton /> : error ? <ErrorState message={error} onRetry={reload} /> : (
        <>
          <Summary s={data.summary} />
          <div className="mb-4 overflow-x-auto">
            <Chips testId="rent-filter" value={filter} onChange={setFilter} options={[
              { value: "all", label: t("all") }, { value: "overdue", label: t("st_overdue") }, { value: "pending", label: t("st_pending") }, { value: "paid", label: t("st_paid") },
            ]} />
          </div>
          {items.length === 0 ? (
            <EmptyState testId="rent-empty" icon={IndianRupee} title={t("no_rent_records")} text={data.items.length ? undefined : t("no_rent_text")} action={data.items.length ? undefined : genBtn} />
          ) : (
            <div className="pg-stagger">{items.map((r) => <RentRow key={r.id} r={r} pg={property.name} onPay={() => setPay(r)} />)}</div>
          )}
        </>
      )}
      <MarkPaidSheet record={pay} onClose={() => setPay(null)} onSaved={reload} />
    </div>
  );
}
